import React, { useEffect, useState } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import LoginLayout from "../../layouts/LoginLayout";
import alertService from "../../components/alert/alertService";
import UsersService from "../../service/users.service";
import { common } from "../../i18n";
import { LOGO_IMG_SRC } from "../../constants";

type UnlockState = "loading" | "success" | "error";

const UnlockPage: React.FC = () => {
  const { t } = useTranslation([common]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [state, setState] = useState<UnlockState>("loading");

  useEffect(() => {
    if (!token) {
      setState("error");
      return;
    }

    const unlock = async () => {
      let result;
      try {
        result = await UsersService.unlockAccount(token);
      } catch {
        // error alert already shown by service
        setState("error");
        return;
      }

      if (result) {
        setState("success");
        await alertService.success(t("Unlock-Account-Success"));
        navigate("/login", { replace: true });
      } else {
        setState("error");
      }
    };

    unlock();
  }, [token, navigate, t]);

  if (!token) {
    return (
      <LoginLayout>
        <Box
          maxWidth={"400px"}
          display={"flex"}
          flexDirection={"column"}
          alignItems="center"
          rowGap={"20px"}
        >
          <img
            src={LOGO_IMG_SRC}
            alt="logo"
            style={{ height: "28px", marginBottom: "20px" }}
          />
          <Typography variant="h5" color="error">
            {t("Unlock-Token-Invalid")}
          </Typography>
          <Typography variant="body2" textAlign="center">
            {t("Unlock-Invalid-Link-Description")}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate("/login")}
            sx={{ mt: 2 }}
          >
            {t("Go-To-Login-Page")}
          </Button>
        </Box>
      </LoginLayout>
    );
  }

  return (
    <LoginLayout>
      <Box
        maxWidth={"400px"}
        display={"flex"}
        flexDirection={"column"}
        alignItems="center"
        rowGap={"20px"}
        width={"100%"}
      >
        <img
          src={LOGO_IMG_SRC}
          alt="logo"
          style={{
            padding: "12px 0px",
            height: "28px",
            width: "233px",
            marginBottom: "20px",
          }}
        />

        <Typography variant="h1" textAlign="center">
          {t("Unlock-Your-Account")}
        </Typography>

        {state === "loading" && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
            mt={2}
          >
            <CircularProgress color="secondary" />
          </Box>
        )}

        {state === "error" && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
            mt={2}
          >
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() => navigate("/login")}
            >
              {t("Go-To-Login-Page")}
            </Button>
          </Box>
        )}
      </Box>
    </LoginLayout>
  );
};

export default UnlockPage;
